/**
 * W66.X16 — In-Platform Guided Tour
 *
 * Route: /#/tour
 * Four role perspectives: BDR (Maya), AE (Jordan), Manager (Riley), Marketing role.
 *
 * Implementation: Custom overlay using fixed-position spotlight with clip-path backdrop.
 * No external dependencies added.
 *
 * Canon:
 * - 5-color palette: gray #6B7280, red #DC2626, orange #EA580C, yellow #EAB308, green #16A34A
 * - Backdrop: #111827 at 0.7 opacity
 * - Role tag colors: BDR orange, AE green, Manager gray, Marketing yellow
 * - M-PACT hyphenated, no exclamation points, no emojis
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { Link } from "wouter";
import { BDR_TOUR_STEPS } from "@/data/tour-scripts/bdr";
import { AE_TOUR_STEPS } from "@/data/tour-scripts/ae";
import { MANAGER_TOUR_STEPS } from "@/data/tour-scripts/manager";
import { MARKETING_TOUR_STEPS } from "@/data/tour-scripts/marketing";
import type { GuidedTourStep, TourRole } from "@/data/tour-scripts/types";

// ── Role config ─────────────────────────────────────────────────────────────

interface RoleConfig {
  id: TourRole;
  label: string;
  persona: string;
  tagColor: string;
  tagBg: string;
  steps: GuidedTourStep[];
  startRoute: string;
}

const ROLES: RoleConfig[] = [
  {
    id: "bdr",
    label: "BDR",
    persona: "BDR",
    tagColor: "#FFFFFF",
    tagBg: "#EA580C",
    steps: BDR_TOUR_STEPS,
    startRoute: "/#/today",
  },
  {
    id: "ae",
    label: "AE",
    persona: "AE",
    tagColor: "#FFFFFF",
    tagBg: "#16A34A",
    steps: AE_TOUR_STEPS,
    startRoute: "/#/today",
  },
  {
    id: "manager",
    label: "Manager",
    persona: "Manager",
    tagColor: "#FFFFFF",
    tagBg: "#6B7280",
    steps: MANAGER_TOUR_STEPS,
    startRoute: "/#/dashboards/manager",
  },
  {
    id: "marketing",
    label: "Marketing",
    persona: "Marketing",
    tagColor: "#1F2937",
    tagBg: "#EAB308",
    steps: MARKETING_TOUR_STEPS,
    startRoute: "/#/funnel",
  },
];

// ── Spotlight geometry ───────────────────────────────────────────────────────

interface SpotRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PADDING = 8;
const CARD_W = 380;
const CARD_GAP = 16;
const RESOLVE_MS = 1800;

function getSpotRect(el: Element): SpotRect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - PADDING,
    left: r.left - PADDING,
    width: r.width + PADDING * 2,
    height: r.height + PADDING * 2,
  };
}

function placeCard(
  spot: SpotRect | null,
  winW: number,
  winH: number,
): { top: number; left: number } {
  if (!spot) {
    return {
      top: Math.max(40, winH / 2 - 180),
      left: Math.max(20, winW / 2 - CARD_W / 2),
    };
  }
  const spaceBelow = winH - (spot.top + spot.height);
  const spaceAbove = spot.top;
  const preferBelow = spaceBelow >= 260 || spaceAbove < 260;

  let top: number;
  if (preferBelow) {
    top = spot.top + spot.height + CARD_GAP;
  } else {
    top = spot.top - 260 - CARD_GAP;
  }
  top = Math.max(12, Math.min(top, winH - 280));

  const rawLeft = spot.left + spot.width / 2 - CARD_W / 2;
  const left = Math.max(12, Math.min(rawLeft, winW - CARD_W - 12));

  return { top, left };
}

// ── Overlay component ────────────────────────────────────────────────────────

interface OverlayProps {
  role: RoleConfig;
  step: GuidedTourStep;
  stepIndex: number;
  total: number;
  onNext: () => void;
  onPrev: () => void;
  onGoTo: (n: number) => void;
  onExit: () => void;
}

function TourStepOverlay({
  role,
  step,
  stepIndex,
  total,
  onNext,
  onPrev,
  onGoTo,
  onExit,
}: OverlayProps) {
  const [spot, setSpot] = useState<SpotRect | null>(null);
  const [missing, setMissing] = useState(false);
  const [winSize, setWinSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const [jumpOpen, setJumpOpen] = useState(false);
  const [jumpVal, setJumpVal] = useState("");

  // Track window size
  useEffect(() => {
    const onResize = () => setWinSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Resolve selector
  useEffect(() => {
    let cancelled = false;
    const start = Date.now();

    const cleanup = () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
    };

    if (!step.selector) {
      setSpot(null);
      setMissing(false);
      return cleanup;
    }

    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector(step.selector);
      if (el) {
        setMissing(false);
        const r = getSpotRect(el);
        setSpot(r);
        try {
          el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
        } catch {
          // ignore
        }
        const sync = () => {
          if (cancelled) return;
          const cur = document.querySelector(step.selector);
          if (cur) setSpot(getSpotRect(cur));
          rafRef.current = window.requestAnimationFrame(sync);
        };
        rafRef.current = window.requestAnimationFrame(sync);
        return;
      }
      if (Date.now() - start > RESOLVE_MS) {
        setMissing(true);
        setSpot(null);
        return;
      }
      timerRef.current = window.setTimeout(tryFind, 120);
    };

    setSpot(null);
    setMissing(false);
    timerRef.current = window.setTimeout(tryFind, 300);

    return cleanup;
  }, [step.selector, step.stepNumber]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (jumpOpen) return;
      if (e.key === "ArrowRight" || e.key === "Enter") {
        e.preventDefault();
        onNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        onPrev();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onExit();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onNext, onPrev, onExit, jumpOpen]);

  const cardPos = placeCard(spot, winSize.w, winSize.h);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;

  const handleJumpSubmit = () => {
    const n = parseInt(jumpVal, 10);
    if (!isNaN(n) && n >= 1 && n <= total) {
      onGoTo(n - 1);
    }
    setJumpOpen(false);
    setJumpVal("");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Guided tour — ${role.label} perspective, step ${stepIndex + 1} of ${total}`}
      style={{ position: "fixed", inset: 0, zIndex: 2147483600, pointerEvents: "none" }}
    >
      {/* Dim backdrop with spotlight cutout */}
      {spot ? (
        <>
          {/* Top strip */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              height: spot.top,
              background: "rgba(17,24,39,0.70)",
              pointerEvents: "auto",
            }}
            onClick={onExit}
          />
          {/* Left strip */}
          <div
            style={{
              position: "fixed",
              top: spot.top,
              left: 0,
              width: spot.left,
              height: spot.height,
              background: "rgba(17,24,39,0.70)",
              pointerEvents: "auto",
            }}
            onClick={onExit}
          />
          {/* Right strip */}
          <div
            style={{
              position: "fixed",
              top: spot.top,
              left: spot.left + spot.width,
              right: 0,
              height: spot.height,
              background: "rgba(17,24,39,0.70)",
              pointerEvents: "auto",
            }}
            onClick={onExit}
          />
          {/* Bottom strip */}
          <div
            style={{
              position: "fixed",
              top: spot.top + spot.height,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(17,24,39,0.70)",
              pointerEvents: "auto",
            }}
            onClick={onExit}
          />
          {/* Spotlight border ring */}
          <div
            style={{
              position: "fixed",
              top: spot.top,
              left: spot.left,
              width: spot.width,
              height: spot.height,
              border: "2px solid rgba(255,255,255,0.55)",
              borderRadius: 6,
              pointerEvents: "none",
              transition: "top 180ms ease, left 180ms ease, width 180ms ease, height 180ms ease",
            }}
          />
        </>
      ) : (
        /* Full backdrop when no target */
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(17,24,39,0.70)",
            pointerEvents: "auto",
          }}
          onClick={onExit}
        />
      )}

      {/* Caption card */}
      <div
        data-tour="guided-tour-card"
        style={{
          position: "fixed",
          top: cardPos.top,
          left: cardPos.left,
          width: CARD_W,
          background: "#FFFFFF",
          color: "#0B0B0F",
          border: "1px solid #E5E7EB",
          borderRadius: 10,
          boxShadow: "0 20px 48px rgba(15,23,42,0.32)",
          padding: "18px 20px",
          pointerEvents: "auto",
          fontFamily: "inherit",
          transition: "top 180ms ease, left 180ms ease",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header row: role tag + step counter + exit */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "2px 10px",
                borderRadius: 999,
                background: role.tagBg,
                color: role.tagColor,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.6,
                textTransform: "uppercase",
              }}
            >
              {role.label} — {role.persona}
            </span>
            <span
              style={{
                fontSize: 11,
                color: "#6B7280",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {stepIndex + 1} / {total}
            </span>
          </div>
          <button
            type="button"
            onClick={onExit}
            aria-label="Exit tour"
            style={{
              border: "1px solid #E5E7EB",
              background: "#FFFFFF",
              color: "#6B7280",
              borderRadius: 6,
              padding: "3px 8px",
              fontSize: 11,
              cursor: "pointer",
              lineHeight: 1.4,
            }}
          >
            Exit
          </button>
        </div>

        {/* Step title */}
        <div
          style={{
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.3,
            marginBottom: 10,
            color: "#111827",
          }}
        >
          {step.title}
        </div>

        {/* Narration */}
        <div
          style={{
            fontSize: 13,
            lineHeight: 1.6,
            color: "#1F2937",
            marginBottom: 12,
          }}
        >
          {step.narration}
        </div>

        {/* Coaching beat */}
        {step.coachingBeat && (
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.55,
              color: "#374151",
              background: "#F9FAFB",
              border: "1px solid #E5E7EB",
              borderLeft: `3px solid ${role.tagBg}`,
              borderRadius: "0 6px 6px 0",
              padding: "8px 10px",
              marginBottom: 12,
              fontStyle: "italic",
            }}
          >
            {step.coachingBeat}
          </div>
        )}

        {/* Missing element notice */}
        {missing && (
          <div
            style={{
              fontSize: 11,
              color: "#92400E",
              background: "#FEF3C7",
              border: "1px solid #FDE68A",
              borderRadius: 6,
              padding: "6px 8px",
              marginBottom: 10,
            }}
          >
            This element is not visible on the current screen. The tour step is still valid — the caption describes what appears here when the page has data.
          </div>
        )}

        {/* Navigation row */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginTop: 4,
          }}
        >
          <button
            type="button"
            onClick={onPrev}
            disabled={isFirst}
            style={{
              border: "1px solid #E5E7EB",
              background: "#FFFFFF",
              color: isFirst ? "#D1D5DB" : "#374151",
              borderRadius: 6,
              padding: "7px 12px",
              fontSize: 12,
              fontWeight: 500,
              cursor: isFirst ? "default" : "pointer",
              minWidth: 70,
            }}
          >
            Previous
          </button>

          {/* Skip-to */}
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setJumpOpen((v) => !v)}
              style={{
                border: "1px solid #E5E7EB",
                background: "#FFFFFF",
                color: "#6B7280",
                borderRadius: 6,
                padding: "7px 10px",
                fontSize: 11,
                cursor: "pointer",
              }}
              aria-label="Skip to step"
            >
              Skip to step
            </button>
            {jumpOpen && (
              <div
                style={{
                  position: "absolute",
                  bottom: "calc(100% + 6px)",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "#FFFFFF",
                  border: "1px solid #E5E7EB",
                  borderRadius: 8,
                  boxShadow: "0 8px 24px rgba(15,23,42,0.18)",
                  padding: 10,
                  display: "flex",
                  gap: 6,
                  alignItems: "center",
                  zIndex: 10,
                  whiteSpace: "nowrap",
                }}
              >
                <input
                  type="number"
                  min={1}
                  max={total}
                  value={jumpVal}
                  onChange={(e) => setJumpVal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleJumpSubmit();
                    if (e.key === "Escape") { setJumpOpen(false); setJumpVal(""); }
                  }}
                  placeholder={`1–${total}`}
                  autoFocus
                  style={{
                    width: 60,
                    border: "1px solid #D1D5DB",
                    borderRadius: 4,
                    padding: "4px 6px",
                    fontSize: 12,
                    outline: "none",
                  }}
                />
                <button
                  type="button"
                  onClick={handleJumpSubmit}
                  style={{
                    background: "#111827",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: 4,
                    padding: "4px 8px",
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Go
                </button>
              </div>
            )}
          </div>

          {isLast ? (
            <button
              type="button"
              onClick={onExit}
              style={{
                border: "1px solid #6B7280",
                background: "#FFFFFF",
                color: "#374151",
                borderRadius: 6,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                minWidth: 70,
              }}
            >
              Finish
            </button>
          ) : (
            <button
              type="button"
              onClick={onNext}
              style={{
                border: `1px solid ${role.tagBg}`,
                background: role.tagBg,
                color: role.tagColor,
                borderRadius: 6,
                padding: "7px 14px",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                minWidth: 70,
              }}
            >
              Next
            </button>
          )}
        </div>

        {/* Keyboard hint */}
        <div
          style={{
            marginTop: 8,
            fontSize: 10,
            color: "#9CA3AF",
            textAlign: "center",
          }}
        >
          Arrow keys to navigate · Esc to exit
        </div>
      </div>

      {/* Step progress dots */}
      <div
        style={{
          position: "fixed",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: 6,
          alignItems: "center",
          pointerEvents: "auto",
          zIndex: 2147483601,
        }}
      >
        {Array.from({ length: total }).map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onGoTo(i)}
            aria-label={`Go to step ${i + 1}`}
            style={{
              width: i === stepIndex ? 20 : 8,
              height: 8,
              borderRadius: 999,
              border: "none",
              background:
                i === stepIndex
                  ? role.tagBg
                  : i < stepIndex
                  ? "rgba(255,255,255,0.6)"
                  : "rgba(255,255,255,0.25)",
              cursor: "pointer",
              padding: 0,
              transition: "width 200ms ease, background 200ms ease",
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Tour landing page ────────────────────────────────────────────────────────

export default function Tour() {
  const [activeRole, setActiveRole] = useState<TourRole | null>(null);
  const [stepIndex, setStepIndex] = useState(0);

  const role = activeRole ? ROLES.find((r) => r.id === activeRole) ?? null : null;
  const steps = role ? role.steps : [];
  const currentStep = steps[stepIndex] ?? null;

  const startTour = useCallback(
    (roleId: TourRole) => {
      const r = ROLES.find((x) => x.id === roleId);
      if (!r) return;
      setActiveRole(roleId);
      setStepIndex(0);
      // Navigate to the first step's route
      const firstStep = r.steps[0];
      if (firstStep) {
        const hash = firstStep.route.startsWith("/#")
          ? firstStep.route.slice(1)
          : firstStep.route;
        window.location.hash = hash;
      }
    },
    [],
  );

  const handleNext = useCallback(() => {
    if (!role) return;
    const next = stepIndex + 1;
    if (next >= role.steps.length) {
      setActiveRole(null);
      setStepIndex(0);
      return;
    }
    const nextStep = role.steps[next];
    if (nextStep) {
      const hash = nextStep.route.startsWith("/#")
        ? nextStep.route.slice(1)
        : nextStep.route;
      window.location.hash = hash;
    }
    setStepIndex(next);
  }, [role, stepIndex]);

  const handlePrev = useCallback(() => {
    if (!role || stepIndex === 0) return;
    const prev = stepIndex - 1;
    const prevStep = role.steps[prev];
    if (prevStep) {
      const hash = prevStep.route.startsWith("/#")
        ? prevStep.route.slice(1)
        : prevStep.route;
      window.location.hash = hash;
    }
    setStepIndex(prev);
  }, [role, stepIndex]);

  const handleGoTo = useCallback(
    (n: number) => {
      if (!role) return;
      if (n < 0 || n >= role.steps.length) return;
      const targetStep = role.steps[n];
      if (targetStep) {
        const hash = targetStep.route.startsWith("/#")
          ? targetStep.route.slice(1)
          : targetStep.route;
        window.location.hash = hash;
      }
      setStepIndex(n);
    },
    [role],
  );

  const handleExit = useCallback(() => {
    setActiveRole(null);
    setStepIndex(0);
    // Return to /tour page
    window.location.hash = "#/tour";
  }, []);

  // Render overlay if tour is active
  if (activeRole && role && currentStep) {
    return (
      <TourStepOverlay
        role={role}
        step={currentStep}
        stepIndex={stepIndex}
        total={steps.length}
        onNext={handleNext}
        onPrev={handlePrev}
        onGoTo={handleGoTo}
        onExit={handleExit}
      />
    );
  }

  // Landing page
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#F9FAFB",
        fontFamily: "inherit",
        padding: "0 0 60px",
      }}
    >
      {/* Page header */}
      <div
        style={{
          background: "#111827",
          color: "#FFFFFF",
          padding: "40px 32px 36px",
        }}
      >
        <div style={{ maxWidth: 860, margin: "0 auto" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 1.2,
              textTransform: "uppercase",
              color: "#9CA3AF",
              marginBottom: 10,
            }}
          >
            Mi6 Group — Attilius Platform
          </div>
          <h1
            style={{
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1.25,
              margin: "0 0 12px",
              letterSpacing: -0.3,
            }}
          >
            Guided Platform Tour
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.65,
              color: "#D1D5DB",
              margin: 0,
              maxWidth: 600,
            }}
          >
            Four role perspectives — BDR, AE, Manager, and Marketing — each with a scripted
            walk-through of the surfaces that matter to that role. Select a perspective to
            begin. Use arrow keys or the Next button to advance.
          </p>
          <div style={{ marginTop: 18 }}>
            <Link
              href="/manuals"
              style={{
                fontSize: 12,
                color: "#9CA3AF",
                textDecoration: "none",
                borderBottom: "1px solid #4B5563",
              }}
            >
              View operating manuals instead
            </Link>
          </div>
        </div>
      </div>

      {/* Role cards */}
      <div
        style={{
          maxWidth: 860,
          margin: "0 auto",
          padding: "40px 32px 0",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
            gap: 20,
          }}
        >
          {ROLES.map((r) => (
            <RoleCard key={r.id} role={r} onStart={() => startTour(r.id)} />
          ))}
        </div>

        {/* About section */}
        <div
          style={{
            marginTop: 48,
            padding: "24px 28px",
            background: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: 10,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#6B7280",
              marginBottom: 12,
            }}
          >
            About this tour
          </div>
          <p
            style={{
              fontSize: 13,
              lineHeight: 1.7,
              color: "#374151",
              margin: "0 0 10px",
            }}
          >
            This tour was built for Bryan K. Short to walk partners, stakeholders, and prospects
            through the platform without depending on perfect data or live impersonation. Each
            role tour includes the consulting and coaching beats — moments where the system talks
            back to the user: suggested next steps, drop-off alerts, what-moved ribbons, and stack
            rank coaching prompts.
          </p>
          <p
            style={{
              fontSize: 13,
              lineHeight: 1.7,
              color: "#374151",
              margin: 0,
            }}
          >
            The tour does not require live data. If an underlying element is empty, the overlay
            highlights the region and the caption explains what would appear there with data.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Role card component ──────────────────────────────────────────────────────

function RoleCard({
  role,
  onStart,
}: {
  role: RoleConfig;
  onStart: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${hovered ? role.tagBg : "#E5E7EB"}`,
        borderRadius: 10,
        padding: "24px 24px 20px",
        transition: "border-color 180ms ease, box-shadow 180ms ease",
        boxShadow: hovered
          ? "0 8px 24px rgba(15,23,42,0.12)"
          : "0 1px 3px rgba(15,23,42,0.06)",
        cursor: "default",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Role tag */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "3px 12px",
            borderRadius: 999,
            background: role.tagBg,
            color: role.tagColor,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.7,
            textTransform: "uppercase",
          }}
        >
          {role.label}
        </span>
        <span style={{ fontSize: 13, color: "#6B7280" }}>{role.persona}</span>
      </div>

      {/* Step previews */}
      <div style={{ marginBottom: 18 }}>
        {role.steps.slice(0, 4).map((s) => (
          <div
            key={s.stepNumber}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              marginBottom: 6,
            }}
          >
            <span
              style={{
                minWidth: 20,
                height: 20,
                borderRadius: 999,
                background: "#F3F4F6",
                color: "#6B7280",
                fontSize: 10,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginTop: 1,
              }}
            >
              {s.stepNumber}
            </span>
            <span style={{ fontSize: 12, color: "#374151", lineHeight: 1.45 }}>
              {s.title.replace(/^Step \d+ — /, "")}
            </span>
          </div>
        ))}
        {role.steps.length > 4 && (
          <div style={{ fontSize: 11, color: "#9CA3AF", marginLeft: 28, marginTop: 4 }}>
            + {role.steps.length - 4} more steps
          </div>
        )}
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={onStart}
        style={{
          width: "100%",
          border: `1px solid ${role.tagBg}`,
          background: hovered ? role.tagBg : "#FFFFFF",
          color: hovered ? role.tagColor : role.tagBg,
          borderRadius: 6,
          padding: "9px 16px",
          fontSize: 13,
          fontWeight: 600,
          cursor: "pointer",
          transition: "background 180ms ease, color 180ms ease",
          letterSpacing: 0.1,
        }}
      >
        Start {role.label} tour ({role.steps.length} steps)
      </button>
    </div>
  );
}
